import { useExperimentsById, useCorpBusinessTabsEnabled } from "@capone/experiments";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  ActionButton,
  ActionLink,
  DesktopPopupModal,
  Header as GenericHeader,
  TRAVEL_CREDIT_AND_OFFER_TITLE,
  HeaderHomePageRightSection,
  HeaderMenuLinkType,
  Icon,
  IconName,
} from "halifax";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  matchPath,
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import {
  B2B_PORTAL_ENDSESSION_PATH,
  CorpBusinessAccount,
  CorpSessionInfo,
  CustomEvents,
  DropdownAction,
  EditTravelerModalState,
  PORTAL_SWITCHED,
  PortalSwitchedProperties,
  PortalTypes,
  RewardsAccount,
  SELECT_EDIT_TRAVELERS,
  SelectEditTravelersProperties,
  VIEWED_CONTACT_MODAL,
  WalletSummaryResponse,
} from "redmond";

import {
  getAdminOrBusinessUrl,
  getUrlWithUtmParams,
  PATH_CORP_FAQ,
  PATH_CORP_BUSINESS,
  PATH_PREMIUM_OFFERS_SHOP,
  useUserContext,
  useUtmParams,
  getTrackingEntryPoint,
} from "@capone/common";
import { fetchTravelWalletOffers } from "../../../../api/v1/takeovers/fetchTravelWalletOffers";
import { trackEvent } from "../../../../api/v1/trackEvent";
import CorporateLogo from "../../../../assets/b2b/capone-corporate-logo.svg";
import {
  AVAILABLE,
  getExperimentVariant,
  TRAVEL_WALLET_CREDIT_EXPERIMENT,
  TRAVEL_WALLET_OFFER_EXPERIMENT,
  useExperiments,
} from "../../../../context/experiments";
import config from "../../../../utils/capone-corporate/config";
import { CAP_ONE_CORP_LOGO_ALT } from "../../../../utils/constants";
import {
  HIDDEN_HEADER_DETAILS_PATHS,
  PATH_AUTH_INVALID_SESSION,
  PATH_CARS,
  PATH_CORP_ACCESS_DENIED,
  PATH_CORP_ACCESS_DENIED_NOT_BETA,
  PATH_FLIGHTS,
  PATH_HOME,
  PATH_HOTELS,
  PATH_HOTELS_FLIGHTS,
  PATH_PREMIER_COLLECTION,
  PATH_SITE_TERMS_OF_USE,
  PATH_STAYS,
  PATH_TERMS,
  PATH_TERMS_OF_SERVICE,
  PATH_TRAVEL_SALE,
  PATH_TRAVEL_WALLET,
  PATH_TRIPS,
} from "../../../../utils/urlPaths";
import * as textConstants from "../../../constants";
import { FunnelEntryTabs } from "../../../Header/components/FunnelEntryTabs";
import { SupportModalContent } from "../../../Header/components/SupportModal";
import { OnboardingModal } from "../../OnboardingModal";
import { SeanEllisSurveyModal } from "../../SeanEllisSurveyModal";
import { RewardsAccountSelection } from "../../../RewardsAccountSelection";
import { MaintenanceBanner } from "../../Maintenance/MaintenanceBanner";
import { useSwitchBusinessAccount } from "../../MultiAccount/useSwitchBusinessAccount";
import "./styles.scss";
import { PATH_VACATION_RENTAL } from "../../../../../../../cap1-modules/hotels-module/src/utils/paths";

interface IHeaderProps {
  language: string;
  locationPath: string;
  rewardsAccounts?: RewardsAccount[];
  selectedRewardsAccountId: string | null;
  setSelectedRewardsAccountId: (id: string | null) => void;
  notAuthenticated?: boolean;
  canViewConsumer: boolean;
  corpBusinessAccounts?: CorpBusinessAccount[];
}

const DesktopHeader = ({
  locationPath,
  rewardsAccounts,
  selectedRewardsAccountId,
  setSelectedRewardsAccountId,
  notAuthenticated,
  canViewConsumer,
  corpBusinessAccounts,
}: IHeaderProps) => {
  const {
    policies,
    sessionInfo,
    modalManagerProps: { activeModal, closeActiveModal },
  } = useUserContext("capone-corporate");
  const { handleChangeBusinessInUse, isAccountSwitchLoading } =
    useSwitchBusinessAccount();
  const history = useHistory();
  const location = useLocation();

  const currentAccount = rewardsAccounts?.find(
    (account) => account.accountReferenceId === selectedRewardsAccountId
  );

  const maintenanceExperiment = useExperimentsById("corp-traveler-maintenance");
  const isMaintenaceBannerActive =
    Boolean(maintenanceExperiment) &&
    maintenanceExperiment?.variant === "banner";

  const BOTTOM_TOOLBAR_PATHS = [
    PATH_FLIGHTS,
    PATH_HOTELS,
    PATH_STAYS,
    PATH_CARS,
    PATH_HOTELS_FLIGHTS,
  ];

  const shouldShowBottomToolbar =
    !notAuthenticated &&
    BOTTOM_TOOLBAR_PATHS.includes(locationPath);

  return (
    <>
      <GenericHeader
        className={clsx("desktop-header", "corp", {
          b2b: !notAuthenticated,
          "unprotected-header": notAuthenticated,
        })}
        fullWidth
        left={
          <Switch>
            <Route
              path={[
                PATH_FLIGHTS,
                PATH_HOTELS,
                PATH_STAYS,
                PATH_CARS,
                PATH_VACATION_RENTAL,
                PATH_HOTELS_FLIGHTS,
                PATH_TRIPS,
                PATH_TERMS,
                PATH_TERMS_OF_SERVICE,
                PATH_SITE_TERMS_OF_USE,
                PATH_PREMIER_COLLECTION,
                PATH_PREMIUM_OFFERS_SHOP,
                PATH_TRAVEL_WALLET,
                PATH_TRAVEL_SALE,
                PATH_CORP_ACCESS_DENIED,
                PATH_CORP_ACCESS_DENIED_NOT_BETA,
                PATH_CORP_FAQ,
                PATH_CORP_BUSINESS,
              ]}
              render={(browserRouterProps: RouteComponentProps) => (
                <div
                  className={clsx("logo-container")}
                  onClick={() => {
                    browserRouterProps.history.push(PATH_HOME);
                  }}
                >
                  <img
                    src={CorporateLogo}
                    data-testid="capone-corporate-header-icon"
                    alt={CAP_ONE_CORP_LOGO_ALT}
                  />
                </div>
              )}
            />
            <Route path="*" />
          </Switch>
        }
        toolbarClassName="b2b"
        right={
          notAuthenticated ? (
            <Switch>
              <Route
                path={[
                  PATH_FLIGHTS,
                  PATH_HOTELS,
                  PATH_CARS,
                  PATH_HOTELS_FLIGHTS,
                  PATH_TRIPS,
                  PATH_TERMS,
                  PATH_TERMS_OF_SERVICE,
                  PATH_SITE_TERMS_OF_USE,
                  PATH_PREMIER_COLLECTION,
                  PATH_PREMIUM_OFFERS_SHOP,
                  PATH_TRAVEL_WALLET,
                  PATH_TRAVEL_SALE,
                ]}
                render={(browserRouterProps: RouteComponentProps) => (
                  <CapOneHeaderHomePageRightSection
                    canViewConsumer={canViewConsumer}
                    notAuthenticated={notAuthenticated}
                    {...browserRouterProps}
                  />
                )}
              />
              <Route path="*" />
            </Switch>
          ) : rewardsAccounts &&
            !HIDDEN_HEADER_DETAILS_PATHS.includes(locationPath) &&
            rewardsAccounts.length > 0 ? (
            <RewardsAccountSelection
              selectedRewardsAccountId={selectedRewardsAccountId}
              setSelectedRewardsAccountId={setSelectedRewardsAccountId}
              rewardsAccounts={rewardsAccounts}
              corpBusinessAccounts={corpBusinessAccounts}
              activeAccountBusinessId={
                (sessionInfo as CorpSessionInfo).corporateInfo.businessId ?? ""
              }
              handleChangeBusinessInUse={handleChangeBusinessInUse}
              isAccountSwitchLoading={isAccountSwitchLoading}
            />
          ) : undefined
        }
        middle={
          isMaintenaceBannerActive && (
            <MaintenanceBanner
              title={maintenanceExperiment.data.title}
              subtext={maintenanceExperiment.data.subtext}
              severity={maintenanceExperiment.data.severity}
            />
          )
        }
        bottom={
          shouldShowBottomToolbar ? (
            <FunnelEntryTabs history={history} location={location} match={{} as any} />
          ) : undefined
        }
      />
      {policies && sessionInfo && (
        <OnboardingModal policies={policies} currentAccount={currentAccount} />
      )}

      <SeanEllisSurveyModal
        isOpen={activeModal === "seanEllis"}
        onClose={() => closeActiveModal("seanEllis")}
      />
    </>
  );
};

interface ICapOneHeaderHomePageRightSectionProps extends RouteComponentProps {
  displayOnlyIcon?: boolean;
  notAuthenticated?: boolean;
  canViewConsumer: boolean;
}

export const CapOneHeaderHomePageRightSection = (
  props: ICapOneHeaderHomePageRightSectionProps
) => {
  const { displayOnlyIcon, notAuthenticated, canViewConsumer, ...routerProps } =
    props;
  const { history } = routerProps;
  const { pathname } = history.location;
  const { sessionInfo } = useUserContext("capone-corporate");

  const expState = useExperiments();
  const utmParams = useUtmParams();
  const businessTabsEnabled = useCorpBusinessTabsEnabled();

  const [openSupportModal, setOpenSupportModal] = useState(false);
  const [offerCount, setOfferCount] = useState(0);
  const onCarsPage = matchPath(pathname, PATH_CARS);
  const onFlightsPage = matchPath(pathname, PATH_FLIGHTS);
  const onHotelsPage = matchPath(pathname, PATH_HOTELS);
  const onStaysPage = matchPath(pathname, PATH_STAYS);
  const onTripsPage = matchPath(pathname, PATH_TRIPS);
  const onWalletPage = matchPath(pathname, PATH_TRAVEL_WALLET);
  const onPackagesPage = matchPath(pathname, PATH_HOTELS_FLIGHTS);
  const dropdownActions: DropdownAction[] = [];

  const consumerPortalUrl = getUrlWithUtmParams(
    config.consumerPortalUrl || "",
    utmParams
  );

  const adminPortalUrl = getUrlWithUtmParams(
    getAdminOrBusinessUrl(undefined, businessTabsEnabled),
    utmParams
  );

  const isTravelWalletOfferExperiment =
    getExperimentVariant(
      expState.experiments,
      TRAVEL_WALLET_OFFER_EXPERIMENT
    ) === AVAILABLE;

  const isTravelWalletCreditsExperiment =
    getExperimentVariant(
      expState.experiments,
      TRAVEL_WALLET_CREDIT_EXPERIMENT
    ) === AVAILABLE;

  const onLogout = useCallback(() => {
    history.push(B2B_PORTAL_ENDSESSION_PATH);
  }, [history]);

  const showSupportModal = () => {
    setOpenSupportModal(true);
  };

  useEffect(() => {
    document.addEventListener(CustomEvents.showSupportModal, showSupportModal);
    return () => {
      document.removeEventListener(
        CustomEvents.showSupportModal,
        showSupportModal
      );
    };
  }, []);

  /**
   * @description Dispatches a custom event to communicate with Pawtucket
   * modules. This event is handled in flights/hotels/cars Body component.
   */
  const onEditTravelerClick = useCallback(
    (iconButton = false) => {
      let editTravelersModalState = EditTravelerModalState.closed;

      if (onCarsPage) {
        editTravelersModalState = EditTravelerModalState.ground;
      } else if (onFlightsPage) {
        editTravelersModalState = EditTravelerModalState.flight;
      } else if (onHotelsPage) {
        editTravelersModalState = EditTravelerModalState.hotel;
      } else if (onStaysPage) {
        editTravelersModalState = EditTravelerModalState.hotel;
      } else if (onTripsPage) {
        editTravelersModalState = EditTravelerModalState.trips;
      } else if (onWalletPage) {
        editTravelersModalState = EditTravelerModalState.wallet;
      } else if (onPackagesPage) {
        editTravelersModalState = EditTravelerModalState.packages;
      }

      const event = new CustomEvent<any>(
        CustomEvents.editTravelersStateChange,
        {
          detail: editTravelersModalState,
        }
      );

      document.dispatchEvent(event);

      trackEvent({
        eventName: SELECT_EDIT_TRAVELERS,
        properties: {
          source: iconButton ? "traveler_nav" : "dropdown, header",
        } satisfies SelectEditTravelersProperties,
      });
    },
    [onCarsPage, onFlightsPage, onHotelsPage, onStaysPage, onTripsPage, onWalletPage, onPackagesPage]
  );

  const handleSupportModalClose = () => setOpenSupportModal(false);

  // only show this action in product pages until edit travelers modal is ready
  // on other pages.
  if (onCarsPage || onFlightsPage || onHotelsPage || onStaysPage) {
    dropdownActions.push({
      divider: true,
      label: "Edit Travelers",
      onClick: onEditTravelerClick,
    });
  }

  useEffect(() => {
    fetchTravelWalletOffers().then((response) => {
      const hasCredit = !!(response as WalletSummaryResponse).credit;
      const offerCount = (response as WalletSummaryResponse).offerCount || 0;
      const totalCount = hasCredit ? offerCount + 1 : offerCount;
      setOfferCount(totalCount);
    });
  }, []);

  const trackPortalSwitched = (portal: PortalTypes) => {
    trackEvent({
      eventName: PORTAL_SWITCHED,
      properties: {
        current_portal: "corporate",
        portal_selected: portal,
      } satisfies PortalSwitchedProperties,
    });
  };

  const links = useMemo(
    () => [
      ...(isTravelWalletCreditsExperiment
        ? [
          {
            type: HeaderMenuLinkType.Wallet,
            selected: history.location.pathname === PATH_TRAVEL_WALLET,
            content: (
              <Typography className="my-travel-offers-link">
                {TRAVEL_CREDIT_AND_OFFER_TITLE}
              </Typography>
            ),
            onClick: () => {
              const entryPoint = window.location.pathname;
              routerProps.history.push(`${PATH_TRAVEL_WALLET}?entry_point=${getTrackingEntryPoint(entryPoint)}`);
            },
            badgeContent: offerCount,
            displayOnlyIcon,
          },
        ]
        : isTravelWalletOfferExperiment
          ? [
            {
              type: HeaderMenuLinkType.Offer,
              selected: history.location.pathname === PATH_TRAVEL_WALLET,
              content: (
                <Typography className="my-travel-offers-link">
                  My travel offers
                </Typography>
              ),
              onClick: () => {
                const entryPoint = window.location.pathname;
                routerProps.history.push(`${PATH_TRAVEL_WALLET}?entry_point=${getTrackingEntryPoint(entryPoint)}`);
              },
              badgeContent: offerCount,
              displayOnlyIcon,
            },
          ]
          : []),
      {
        type: HeaderMenuLinkType.Trips,
        selected: history.location.pathname === PATH_TRIPS,
        content: <Typography className="my-trips-link">My trips</Typography>,
        onClick: () => {
          const entryPoint = window.location.pathname;
          routerProps.history.push(`${PATH_TRIPS}?entry_point=${getTrackingEntryPoint(entryPoint)}`);
        },
        dropdownActions: [],
        displayOnlyIcon,
      },
      {
        type: HeaderMenuLinkType.Travelers,
        selected: false,
        content: <Typography>Traveler profiles</Typography>,
        onClick: () => onEditTravelerClick(true),
        dropdownActions: [],
        displayOnlyIcon,
      },
      {
        type: HeaderMenuLinkType.Support,
        selected: false,
        content: <Typography>{textConstants.SUPPORT_LINK}</Typography>,
        onClick: () => {
          trackEvent({
            eventName: VIEWED_CONTACT_MODAL,
            properties: { contact_reason: "general_contact" },
          });
          setOpenSupportModal(true);
        },
        dropdownActions: [],
        displayOnlyIcon,
      },
      {
        type: HeaderMenuLinkType.CorporateMenu,
        selected: false,
        content: displayOnlyIcon ? (
          <>
            <Icon name={IconName.B2BUser} />
            <Typography>{` ${sessionInfo?.userInfo?.firstName}`}</Typography>
          </>
        ) : (
          <Typography>{`Welcome, ${sessionInfo?.userInfo?.firstName}`}</Typography>
        ),
        onClick: onLogout,
        dropdownActions: [
          {
            label: `${sessionInfo?.userInfo?.firstName} ${sessionInfo?.userInfo?.lastName}`,
            onClick: onEditTravelerClick,
          },
        ],
      },
    ],
    [
      displayOnlyIcon,
      history.location.pathname,
      isTravelWalletCreditsExperiment,
      isTravelWalletOfferExperiment,
      offerCount,
      onEditTravelerClick,
      onLogout,
      routerProps.history,
      sessionInfo?.userInfo?.firstName,
      sessionInfo?.userInfo?.lastName,
    ]
  );

  const UnprotectedPathHeaderRightSection = () => (
    <Box className="non-auth-header-right-section-container">
      <Box className="non-auth-header-right-section-support">
        <Typography className="support-text">
          {textConstants.QUESTIONS_ABOUT_CAP1_TRAVEL}
        </Typography>
        <ActionLink
          content={
            <Typography className="support-modal-link-text">
              {textConstants.GET_HELP}
            </Typography>
          }
          onClick={() => {
            trackEvent({
              eventName: VIEWED_CONTACT_MODAL,
              properties: { contact_reason: "general_contact" },
            });
            setOpenSupportModal(true);
          }}
          className="support-modal-link"
        />
      </Box>
      <ActionButton
        onClick={() => {
          history.push(PATH_AUTH_INVALID_SESSION);
        }}
        message={textConstants.SIGN_IN}
        className="b2b non-auth-login-button"
      />
    </Box>
  );
  return (
    <>
      {notAuthenticated ? (
        <UnprotectedPathHeaderRightSection />
      ) : (
        <HeaderHomePageRightSection
          links={links}
          {...routerProps}
          className="b2b"
          consumerPortalUrl={consumerPortalUrl}
          adminPortalUrl={adminPortalUrl}
          canViewAdmin={
            (sessionInfo as CorpSessionInfo)?.corporateInfo.permissions
              .canViewAdmin
          }
          canViewConsumer={canViewConsumer}
          tenant="capone-corporate"
          trackPortalSwitched={trackPortalSwitched}
        />
      )}
      <DesktopPopupModal
        open={openSupportModal}
        className="desktop-contact-support-popup"
        onClose={handleSupportModalClose}
        invisibleBackdrop={false}
      >
        <Box className="desktop-contact-support-popup-content">
          <SupportModalContent />
        </Box>
      </DesktopPopupModal>
    </>
  );
};

export default DesktopHeader;
